/**
 * @documental
 * @export TooltipContainer
 * @import
 * import { TooltipContainer } from "capitalrx-components-2.0/lib/general";
 */

import { FC, useState, useMemo, useCallback, PropsWithChildren } from "react";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import classNames from "classnames";
import { TooltipContext, TooltipContextValue } from "./TooltipContext";
import styles from "./styles.module.scss";

/**
 * The `TooltipContainer` component properties.
 */
export interface TooltipContainerProps {
  /**
   * The component test ID which can be used by tests to locate the component.
   */
  readonly testId?: string;
}

/**
 * The container for all descendent `Tooltip` instances. Acts as the surface upon which all
 * tooltips are placed. If no `TooltipContainer` exists then tooltips will not display.
 *
 * Any tooltips as descendents to the `TooltipContainer` will attempt to constrain their position
 * to the container's bounding area.
 */
export const TooltipContainer: FC<PropsWithChildren<TooltipContainerProps>> = (props) => {
  const { children, testId } = props;
  const baseHtmlProps = getHtmlPropsFromBaseProps(
    { testId },
    {
      className: [styles.TooltipContainer, "tooltip-container"],
      testIdPrefix: "tooltip-container",
    },
  );

  const [element, setElement] = useState<HTMLElement | null>(null);

  const elementRefHandler = useCallback((ref: HTMLElement | null) => {
    setElement(ref);
  }, []);

  const contextValue = useMemo(
    (): TooltipContextValue => ({
      element,
    }),
    [element],
  );

  return (
    <>
      <TooltipContext.Provider value={contextValue}>{children}</TooltipContext.Provider>
      <div {...baseHtmlProps}>
        <div ref={elementRefHandler} className={classNames(styles.TooltipContainerContent, "tooltip-container-content")} />
      </div>
    </>
  );
};
